import Phaser from 'phaser';
import { socketClient } from '../net/SocketClient';
import type { ChatMessage, GameState, RoomInfo, OngoingGameInfo, Side } from '~shared/types';
import { openRulesPanel, destroyRulesPanel } from '../rulesPanel';
import { scenarioSelectOptionsHTML } from '../data/scenarioList';
import { getClientId } from '../net/clientId';

export class LobbyScene extends Phaser.Scene {
  constructor() { super({ key: 'LobbyScene' }); }

  create(): void {
    // Phaser does not call a `shutdown()` method automatically — it must be
    // wired to the scene's `shutdown` event, otherwise socket listeners from
    // this run stay registered after the scene restarts and stack up across
    // repeated visits to the lobby.
    this.events.once('shutdown', this.shutdown, this);

    const isTouch = this.sys.game.device.input.touch;

    // Allow browser pinch-zoom on the lobby — easier to navigate on small screens.
    // The game scene restores user-scalable=no when it starts.
    const vp = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    if (vp) vp.content = 'width=device-width, initial-scale=1.0';

    const W = 1200, H = 768;
    this.add.rectangle(W / 2, H / 2, W, H, 0x1a2d1a);
    this.add.text(W / 2, 60, "MEMOIR '44", {
      fontSize: isTouch ? '72px' : '52px', color: '#d4af37', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.add.text(W / 2, 115, 'Online Multiplayer', {
      fontSize: isTouch ? '28px' : '20px', color: '#aaaaaa',
    }).setOrigin(0.5);

    const form = this.add.dom(W / 2, H / 2 + 20).createFromHTML(this.buildHTML(isTouch));

    // Wire up events after DOM is ready
    form.addListener('click');
    form.on('click', (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const id = target.id;
      if (id === 'tabHost')    this.switchTab(form, 'host');
      if (id === 'tabJoin')    this.switchTab(form, 'join');
      if (id === 'tabSolo')    this.switchTab(form, 'solo');
      if (id === 'hostBtn')    this.handleHost(form);
      if (id === 'joinBtn')    this.handleJoin(form);
      if (id === 'soloBtn')    this.handleSolo(form);
      if (id === 'rulesBtn')   openRulesPanel(socketClient.socket);
      if (id === 'tournamentBtn') this.scene.start('TournamentLobbyScene');
      if (target.classList.contains('resume-game-btn')) {
        this.handleResumeGame(form, {
          roomId:     target.dataset['roomid']!,
          playerName: target.dataset['playername']!,
          side:       target.dataset['side'] as Side,
          status:     target.dataset['status'] as 'waiting' | 'playing',
        });
      }
    });
    form.addListener('keydown');
    form.on('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const active = (form.node as HTMLElement).querySelector('.tab-panel:not([style*="none"])');
        if (active?.id === 'panelHost') this.handleHost(form);
        if (active?.id === 'panelJoin') this.handleJoin(form);
        if (active?.id === 'panelSolo') this.handleSolo(form);
      }
    });

    // Connect socket immediately so we can receive room list
    const socket = socketClient.connect(window.location.origin);
    const clientId = getClientId();
    socket.on('rooms_updated', (rooms: RoomInfo[]) => {
      this.updateRoomList(form, rooms);
    });
    socket.on('your_games_updated', (games: OngoingGameInfo[]) => {
      this.updateYourGames(form, games);
    });
    socket.on('connect', () => {
      socket.emit('list_rooms', { clientId });
    });
    if (socket.connected) socket.emit('list_rooms', { clientId });
  }

  private buildHTML(isTouch = false): string {
    const formW   = isTouch ? '1050px' : '700px';
    const lblSz   = isTouch ? '18px'   : '12px';
    const inputSz = isTouch ? '24px'   : '15px';
    const inputPd = isTouch ? '14px 16px' : '9px 12px';
    const btnSz   = isTouch ? '24px'   : '17px';
    const btnPd   = isTouch ? '18px 0' : '12px 0';
    const tabSz   = isTouch ? '22px'   : '15px';
    const tabPd   = isTouch ? '16px'   : '10px';
    const inp = `padding:${inputPd}; width:100%; box-sizing:border-box; font-size:${inputSz};
                 border-radius:5px; border:1px solid #555; background:#152a15; color:#eee; outline:none;`;
    const yourGamesMaxH = isTouch ? '220px' : '160px';
    return `
    <div style="font-family:Arial,sans-serif; color:#eee; width:${formW};">
      <div id="yourGamesSection" style="display:none; margin-bottom:16px; max-height:${yourGamesMaxH}; overflow-y:auto;"></div>

      <!-- Tabs -->
      <div style="display:flex; margin-bottom:0; gap:4px;">
        <button id="tabHost" style="
          flex:1; padding:${tabPd}; font-size:${tabSz}; cursor:pointer;
          background:#2a4a2a; color:#d4af37; border:1px solid #3a6a3a;
          border-bottom:none; border-radius:6px 6px 0 0; font-weight:bold;">
          Host Game
        </button>
        <button id="tabJoin" style="
          flex:1; padding:${tabPd}; font-size:${tabSz}; cursor:pointer;
          background:#1e3a1e; color:#aaa; border:1px solid #2a4a2a;
          border-bottom:none; border-radius:6px 6px 0 0;">
          Join Game
        </button>
        <button id="tabSolo" style="
          flex:1; padding:${tabPd}; font-size:${tabSz}; cursor:pointer;
          background:#1e3a1e; color:#aaa; border:1px solid #2a4a2a;
          border-bottom:none; border-radius:6px 6px 0 0;">
          vs Computer
        </button>
      </div>

      <div style="background:#1e3a1e; border:1px solid #3a6a3a; border-radius:0 0 6px 6px; padding:20px;">

        <!-- Host panel -->
        <div id="panelHost">
          <div style="display:flex; gap:16px; flex-wrap:wrap;">
            <div style="flex:1; min-width:180px;">
              <label style="display:block; margin-bottom:4px; font-size:${lblSz}; color:#aaa;">Your Name</label>
              <input id="hostName" type="text" maxlength="20" placeholder="e.g. Monty"
                style="${inp}">
            </div>
            <div style="flex:1; min-width:140px;">
              <label style="display:block; margin-bottom:4px; font-size:${lblSz}; color:#aaa;">Your Side</label>
              <select id="hostSide" style="${inp} cursor:pointer;">
                <option value="allies">🔵 Allies</option>
                <option value="axis">🔴 Axis</option>
              </select>
            </div>
          </div>
          <div style="display:flex; gap:16px; flex-wrap:wrap; margin-top:12px;">
            <div style="flex:1; min-width:180px;">
              <label style="display:block; margin-bottom:4px; font-size:${lblSz}; color:#aaa;">Scenario</label>
              <select id="hostScenario" style="${inp} cursor:pointer;">
                ${scenarioSelectOptionsHTML()}
              </select>
            </div>
            <div style="flex:1; min-width:140px;">
              <label style="display:block; margin-bottom:4px; font-size:${lblSz}; color:#aaa;">Password (optional)</label>
              <input id="hostPassword" type="text" maxlength="8" placeholder="leave blank = open"
                style="${inp} letter-spacing:2px; text-transform:uppercase;">
            </div>
          </div>
          <button id="hostBtn"
            style="margin-top:18px; padding:${btnPd}; width:100%; font-size:${btnSz}; cursor:pointer;
                   background:#d4af37; color:#1a1a1a; border:none; border-radius:5px; font-weight:bold;">
            Create Room
          </button>
          <p id="hostStatus" style="margin-top:10px; color:#aaa; font-size:${lblSz}; min-height:18px; text-align:center;"></p>
          <div id="hostWaiting" style="display:none; margin-top:10px; text-align:center;">
            <p style="color:#d4af37; font-size:${lblSz}; margin:0 0 6px;">
              Room created! Share this code with your opponent:
            </p>
            <div id="roomCodeDisplay" style="
              font-size:32px; font-weight:bold; letter-spacing:8px; color:#fff;
              background:#0a1a0a; border:1px solid #d4af37; border-radius:6px;
              padding:10px 24px; display:inline-block; margin-bottom:8px;"></div>
            <p id="hostPasswordDisplay" style="color:#aaa; font-size:${lblSz}; margin:0;"></p>
          </div>
        </div>

        <!-- Join panel -->
        <div id="panelJoin" style="display:none;">
          <div style="display:flex; gap:16px; flex-wrap:wrap; margin-bottom:12px;">
            <div style="flex:1; min-width:180px;">
              <label style="display:block; margin-bottom:4px; font-size:${lblSz}; color:#aaa;">Your Name</label>
              <input id="joinName" type="text" maxlength="20" placeholder="e.g. Rommel"
                style="${inp}">
            </div>
            <div style="flex:1; min-width:140px;">
              <label style="display:block; margin-bottom:4px; font-size:${lblSz}; color:#aaa;">Password</label>
              <input id="joinPassword" type="text" maxlength="8" placeholder="if required"
                style="${inp} letter-spacing:2px; text-transform:uppercase;">
            </div>
          </div>

          <div style="background:#0a1a0a; border:1px solid #2a4a2a; border-radius:5px; padding:8px; min-height:90px; max-height:200px; overflow-y:auto;">
            <div id="roomList" style="color:#888; font-size:${lblSz}; text-align:center; padding:20px 0;">
              Searching for open rooms…
            </div>
          </div>

          <button id="joinBtn"
            style="margin-top:12px; padding:${btnPd}; width:100%; font-size:${btnSz}; cursor:pointer;
                   background:#d4af37; color:#1a1a1a; border:none; border-radius:5px; font-weight:bold;">
            Join Selected Room
          </button>
          <p id="joinStatus" style="margin-top:10px; color:#aaa; font-size:${lblSz}; min-height:18px; text-align:center;"></p>
        </div>

        <!-- Solo panel -->
        <div id="panelSolo" style="display:none;">
          <p style="color:#88aa88; font-size:${lblSz}; margin:0 0 14px;">
            Play against a computer opponent. The computer uses simple heuristics — good for learning the rules.
          </p>
          <div style="display:flex; gap:16px; flex-wrap:wrap;">
            <div style="flex:1; min-width:180px;">
              <label style="display:block; margin-bottom:4px; font-size:${lblSz}; color:#aaa;">Your Name</label>
              <input id="soloName" type="text" maxlength="20" placeholder="e.g. Monty"
                style="${inp}">
            </div>
            <div style="flex:1; min-width:140px;">
              <label style="display:block; margin-bottom:4px; font-size:${lblSz}; color:#aaa;">Your Side</label>
              <select id="soloSide" style="${inp} cursor:pointer;">
                <option value="allies">🔵 Allies</option>
                <option value="axis">🔴 Axis</option>
              </select>
            </div>
          </div>
          <div style="margin-top:12px;">
            <label style="display:block; margin-bottom:4px; font-size:${lblSz}; color:#aaa;">Scenario</label>
            <select id="soloScenario" style="${inp} cursor:pointer;">
              ${scenarioSelectOptionsHTML()}
            </select>
          </div>
          <button id="soloBtn"
            style="margin-top:18px; padding:${btnPd}; width:100%; font-size:${btnSz}; cursor:pointer;
                   background:#d4af37; color:#1a1a1a; border:none; border-radius:5px; font-weight:bold;">
            Start vs Computer
          </button>
          <p id="soloStatus" style="margin-top:10px; color:#aaa; font-size:${lblSz}; min-height:18px; text-align:center;"></p>
        </div>

      </div>
      <div style="margin-top:12px; display:flex; justify-content:space-between; align-items:center;">
        <a href="/editor" target="_blank" rel="noopener"
           style="font-size:11px; color:#556655; text-decoration:none; font-family:Arial,sans-serif;"
           onmouseover="this.style.color='#88aa88'" onmouseout="this.style.color='#556655'">✏ Scenario Editor</a>
        <div style="display:flex; gap:8px;">
          <button id="tournamentBtn" style="padding:6px 14px; font-size:12px; background:#243824;
            color:#d4af37; border:1px solid #6a5a2a; border-radius:4px; cursor:pointer;
            font-family:Arial,sans-serif;">🏆 Tournament</button>
          <button id="rulesBtn" style="padding:6px 14px; font-size:12px; background:#243824;
            color:#aabbaa; border:1px solid #3a6a3a; border-radius:4px; cursor:pointer;
            font-family:Arial,sans-serif;">📖 Rules?</button>
        </div>
      </div>
    </div>`;
  }

  private switchTab(form: Phaser.GameObjects.DOMElement, tab: 'host' | 'join' | 'solo'): void {
    const panelHost = form.getChildByID('panelHost') as HTMLElement;
    const panelJoin = form.getChildByID('panelJoin') as HTMLElement;
    const panelSolo = form.getChildByID('panelSolo') as HTMLElement;
    const tabHost   = form.getChildByID('tabHost') as HTMLButtonElement;
    const tabJoin   = form.getChildByID('tabJoin') as HTMLButtonElement;
    const tabSolo   = form.getChildByID('tabSolo') as HTMLButtonElement;

    const activeStyle   = { background: '#2a4a2a', color: '#d4af37' };
    const inactiveStyle = { background: '#1e3a1e', color: '#aaa' };

    const setTab = (btn: HTMLButtonElement, active: boolean) => {
      const s = active ? activeStyle : inactiveStyle;
      btn.style.background = s.background; btn.style.color = s.color;
    };

    panelHost.style.display = tab === 'host' ? '' : 'none';
    panelJoin.style.display = tab === 'join' ? '' : 'none';
    panelSolo.style.display = tab === 'solo' ? '' : 'none';
    setTab(tabHost, tab === 'host');
    setTab(tabJoin, tab === 'join');
    setTab(tabSolo, tab === 'solo');
  }

  private updateYourGames(form: Phaser.GameObjects.DOMElement, games: OngoingGameInfo[]): void {
    const section = form.getChildByID('yourGamesSection') as HTMLElement | null;
    if (!section) return;

    if (games.length === 0) {
      section.style.display = 'none';
      section.innerHTML = '';
      return;
    }

    const rows = games.map(g => {
      const icon = g.tournamentId ? '🏆' : g.isSinglePlayer ? '🤖' : '👥';
      const sideLabel = g.mySide === 'allies' ? '🔵 Allies' : '🔴 Axis';
      let status: string;
      if (g.status === 'waiting') status = 'Waiting for opponent';
      else if (g.isMyTurn) status = 'Your turn';
      else status = g.isSinglePlayer ? "Computer's turn" : "Opponent's turn";

      return `
        <div style="
          background:#1a3a2a; border:1px solid #d4af37; border-radius:6px;
          padding:10px 18px; margin-bottom:8px; display:flex;
          align-items:center; justify-content:space-between; gap:12px;">
          <span style="color:#d4af37; font-size:13px;">
            ${icon} <strong>${g.scenarioName}</strong> as ${sideLabel} — ${status}${g.tournamentId ? ' <span style="color:#88aa88;">(tournament match)</span>' : ''}
          </span>
          <button class="resume-game-btn"
            data-roomid="${g.roomId}" data-playername="${g.playerName}"
            data-side="${g.mySide}" data-status="${g.status}"
            style="padding:6px 14px; background:#d4af37; color:#111;
            border:none; border-radius:4px; cursor:pointer; font-weight:bold; font-size:13px; flex-shrink:0;">
            Resume
          </button>
        </div>`;
    }).join('');

    section.innerHTML = `
      <div style="color:#88aa88; font-size:12px; margin-bottom:6px;">Your ongoing games</div>
      ${rows}`;
    section.style.display = '';
  }

  private updateRoomList(form: Phaser.GameObjects.DOMElement, rooms: RoomInfo[]): void {
    const listEl = form.getChildByID('roomList') as HTMLElement;
    if (!listEl) return;

    if (rooms.length === 0) {
      listEl.innerHTML = '<div style="color:#666; font-size:13px; text-align:center; padding:20px 0;">No open rooms — be the first to host!</div>';
      return;
    }

    listEl.innerHTML = rooms.map(r => {
      const ago = Math.floor((Date.now() - r.createdAt) / 60000);
      const agoStr = ago < 1 ? 'just now' : `${ago}m ago`;
      const lock = r.hasPassword ? ' 🔒' : '';
      const isPlaying = r.status === 'playing';
      const statusBadge = isPlaying
        ? '<span style="background:#5a3a00; color:#d4af37; font-size:10px; padding:2px 6px; border-radius:3px; margin-right:4px;">IN PROGRESS</span>'
        : '';
      const sideInfo = isPlaying
        ? '<span style="color:#888; font-size:12px;">Rejoin only</span>'
        : `<span style="color:#88aa88; font-size:12px;">${r.openSide === 'allies' ? '🔵 Allies open' : '🔴 Axis open'}</span>`;
      const border = isPlaying ? 'border-bottom:1px solid #2a2a1a' : 'border-bottom:1px solid #1a2a1a';
      return `
        <div class="room-row" data-roomid="${r.roomId}" data-status="${r.status}" style="
          display:flex; align-items:center; gap:10px; padding:8px 10px;
          ${border}; cursor:pointer; border-radius:4px;">
          <span style="font-weight:bold; font-size:15px; letter-spacing:2px; color:${isPlaying ? '#aaa' : '#fff'}; min-width:60px;">${r.roomId}</span>
          <span style="flex:1; color:#ccc; font-size:13px;">${statusBadge}${r.scenarioName}${lock}</span>
          ${sideInfo}
          <span style="color:#888; font-size:11px;">${r.hostName} · ${agoStr}</span>
        </div>`;
    }).join('');

    // Row selection
    listEl.querySelectorAll('.room-row').forEach(row => {
      const el = row as HTMLElement;
      const isPlaying = el.dataset['status'] === 'playing';
      el.addEventListener('mouseenter', () => { el.style.background = isPlaying ? '#1a1a0a' : '#1a3a1a'; });
      el.addEventListener('mouseleave', () => {
        if (!el.classList.contains('selected')) el.style.background = '';
      });
      el.addEventListener('click', () => {
        listEl.querySelectorAll('.room-row').forEach(r => {
          (r as HTMLElement).style.background = '';
          (r as HTMLElement).classList.remove('selected');
        });
        el.style.background = isPlaying ? '#3a3a10' : '#2a5a2a';
        el.classList.add('selected');
      });
    });
  }

  private handleHost(form: Phaser.GameObjects.DOMElement): void {
    const name       = (form.getChildByID('hostName') as HTMLInputElement).value.trim();
    const side       = (form.getChildByID('hostSide') as HTMLSelectElement).value as Side;
    const scenarioId = (form.getChildByID('hostScenario') as HTMLSelectElement).value;
    const password   = (form.getChildByID('hostPassword') as HTMLInputElement).value.trim().toUpperCase() || undefined;
    const statusEl   = form.getChildByID('hostStatus') as HTMLElement;

    if (!name) { statusEl.textContent = 'Please enter your name.'; return; }

    statusEl.textContent = 'Creating room…';
    (form.getChildByID('hostBtn') as HTMLButtonElement).disabled = true;

    const socket = socketClient.connect(window.location.origin);

    const doCreate = () => {
      socket.emit('create_room', { playerName: name, hostSide: side, scenarioId, password, clientId: getClientId() });
    };

    // Captured locally (not read back from socketClient.mySide) so a stale
    // persistent listener from a previous game (e.g. GameScene's
    // `player_joined` reconnect handler) can't race this value before startGame reads it.
    let myAssignedSide: Side | null = null;

    socket.once('player_joined', (info) => {
      myAssignedSide = info.side;
      socketClient.mySide = info.side;
    });

    socket.once('game_state', (state) => {
      // Host sees game_state with roomId immediately after creation
      const waitingEl = form.getChildByID('hostWaiting') as HTMLElement;
      const codeEl    = form.getChildByID('roomCodeDisplay') as HTMLElement;
      const pwEl      = form.getChildByID('hostPasswordDisplay') as HTMLElement;
      waitingEl.style.display = 'block';
      codeEl.textContent = state.roomId;
      pwEl.textContent   = password ? `Password: ${password}` : 'No password';
      statusEl.textContent = '';

      // Now listen for opponent joining
      socket.once('game_start', (startState: GameState) => {
        const mySide = myAssignedSide ?? socketClient.mySide!;
        this.startGame({ gameState: startState, mySide, roomId: startState.roomId, playerName: name });
      });
    });

    socket.on('error', (msg: string) => {
      statusEl.textContent = `Error: ${msg}`;
      (form.getChildByID('hostBtn') as HTMLButtonElement).disabled = false;
    });

    if (socket.connected) doCreate();
    else socket.once('connect', doCreate);
  }

  private handleJoin(form: Phaser.GameObjects.DOMElement): void {
    const name      = (form.getChildByID('joinName') as HTMLInputElement).value.trim();
    const password  = (form.getChildByID('joinPassword') as HTMLInputElement).value.trim().toUpperCase() || undefined;
    const statusEl  = form.getChildByID('joinStatus') as HTMLElement;
    const listEl    = form.getChildByID('roomList') as HTMLElement;

    if (!name) { statusEl.textContent = 'Please enter your name.'; return; }

    const selectedRow = listEl.querySelector('.room-row.selected') as HTMLElement | null;
    if (!selectedRow) { statusEl.textContent = 'Select a room from the list first.'; return; }

    const roomId = selectedRow.dataset['roomid']!;
    statusEl.textContent = 'Joining…';
    (form.getChildByID('joinBtn') as HTMLButtonElement).disabled = true;

    const socket = socketClient.connect(window.location.origin);

    const doJoin = () => {
      socket.emit('join_room', { roomId, playerName: name, password, clientId: getClientId() });
    };

    // Captured locally — see comment in handleHost on why we don't trust
    // socketClient.mySide alone here.
    let myAssignedSide: Side | null = null;
    // Server sends this once, immediately after player_joined — must be captured
    // here (before the scene transition) or it's lost; GameScene registers its
    // listeners too late to catch it.
    let chatHistory: ChatMessage[] = [];
    socket.once('chat_history', (messages) => { chatHistory = messages; });

    const onPlayerJoined = (info: { side: Side; playerCount: number }) => {
      myAssignedSide = info.side;
      socketClient.mySide = info.side;
      statusEl.textContent = `Joined as ${info.side === 'allies' ? '🔵 Allies' : '🔴 Axis'}. Waiting for game to start…`;
    };
    socket.on('player_joined', onPlayerJoined);

    const startGame = (state: GameState) => {
      socket.off('game_state', onGameState);
      socket.off('game_start', startGame);
      socket.off('player_joined', onPlayerJoined);
      const mySide = myAssignedSide ?? socketClient.mySide!;
      this.startGame({ gameState: state, mySide, roomId, playerName: name, chatHistory });
    };

    // Reconnect path: in-progress game sends game_state (not game_start)
    const onGameState = (state: GameState) => {
      if (state.phase !== 'waiting') startGame(state);
    };

    socket.on('game_state', onGameState);
    socket.once('game_start', startGame);

    socket.on('error', (msg: string) => {
      statusEl.textContent = `Error: ${msg}`;
      (form.getChildByID('joinBtn') as HTMLButtonElement).disabled = false;
    });

    if (socket.connected) doJoin();
    else socket.once('connect', doJoin);
  }

  private handleSolo(form: Phaser.GameObjects.DOMElement): void {
    const name       = (form.getChildByID('soloName') as HTMLInputElement).value.trim();
    const side       = (form.getChildByID('soloSide') as HTMLSelectElement).value as Side;
    const scenarioId = (form.getChildByID('soloScenario') as HTMLSelectElement).value;
    const statusEl   = form.getChildByID('soloStatus') as HTMLElement;

    if (!name) { statusEl.textContent = 'Please enter your name.'; return; }

    statusEl.textContent = 'Starting…';
    (form.getChildByID('soloBtn') as HTMLButtonElement).disabled = true;

    const socket = socketClient.connect(window.location.origin);

    const doStart = () => {
      socket.emit('create_solo_game', { playerName: name, playerSide: side, scenarioId, clientId: getClientId() });
    };

    let myAssignedSide: Side | null = null;

    socket.once('player_joined', (info) => {
      myAssignedSide = info.side;
      socketClient.mySide = info.side;
    });

    socket.once('game_start', (state: GameState) => {
      const mySide = myAssignedSide ?? side;
      this.startGame({ gameState: state, mySide, roomId: state.roomId, playerName: name });
    });

    socket.on('error', (msg: string) => {
      statusEl.textContent = `Error: ${msg}`;
      (form.getChildByID('soloBtn') as HTMLButtonElement).disabled = false;
    });

    if (socket.connected) doStart();
    else socket.once('connect', doStart);
  }

  /** Resumes a game from the "Your ongoing games" list — solo or online, waiting or in-progress. */
  private handleResumeGame(form: Phaser.GameObjects.DOMElement, game: { roomId: string; playerName: string; side: Side; status: 'waiting' | 'playing' }): void {
    const statusEl = form.getChildByID('hostStatus') as HTMLElement | null
                  ?? form.getChildByID('joinStatus') as HTMLElement;

    const socket = socketClient.connect(window.location.origin);

    const doResume = () => {
      socket.emit('join_room', { roomId: game.roomId, playerName: game.playerName, clientId: getClientId() });
    };

    // Captured locally — see comment in handleHost on why we don't trust
    // socketClient.mySide alone here.
    let myAssignedSide: Side | null = null;
    // Server sends this once, immediately after player_joined — must be captured
    // here (before the scene transition) or it's lost; GameScene registers its
    // listeners too late to catch it.
    let chatHistory: ChatMessage[] = [];
    socket.once('chat_history', (messages) => { chatHistory = messages; });

    const onPlayerJoined = (info: { side: Side; playerCount: number }) => {
      myAssignedSide = info.side;
      socketClient.mySide = info.side;
    };
    socket.on('player_joined', onPlayerJoined);

    const startGameNow = (state: GameState) => {
      socket.off('game_state', onGameState);
      socket.off('game_start', startGameNow);
      socket.off('player_joined', onPlayerJoined);
      const mySide = myAssignedSide ?? game.side;
      this.startGame({ gameState: state, mySide, roomId: game.roomId, playerName: game.playerName, chatHistory });
    };

    // Reconnecting to an in-progress game gets game_state (not game_start)
    const onGameState = (state: GameState) => {
      if (state.phase !== 'waiting') { startGameNow(state); return; }
      // Host re-opening a room that's still waiting for an opponent — show the
      // host "waiting" view again; game_start follows once someone joins.
      socket.off('game_state', onGameState);
      this.switchTab(form, 'host');
      const waitingEl = form.getChildByID('hostWaiting') as HTMLElement;
      const codeEl    = form.getChildByID('roomCodeDisplay') as HTMLElement;
      waitingEl.style.display = 'block';
      codeEl.textContent = state.roomId;
    };

    socket.on('game_state', onGameState);
    socket.once('game_start', startGameNow);

    socket.on('error', (msg: string) => {
      if (statusEl) statusEl.textContent = `Resume failed: ${msg}`;
    });

    if (socket.connected) doResume();
    else socket.once('connect', doResume);
  }

  /** Reset browser zoom to 1× before starting the game so Scale.FIT is calculated correctly. */
  private startGame(data: object): void {
    const vp = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    if (vp) vp.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    // Give the browser one frame to apply the viewport change, then let Phaser refresh its scale.
    requestAnimationFrame(() => {
      this.scale.refresh();
      this.scene.start('GameScene', data);
    });
  }

  shutdown(): void {
    const s = socketClient.socket;
    s.off('rooms_updated');
    s.off('your_games_updated');
    s.off('connect');
    s.off('player_joined');
    // Phaser's scene.start() queues 'stop' (this scene) before 'start' (the next
    // scene), and stop runs shutdown synchronously — so this fires before
    // GameScene.create() registers its own game_state handler. Safe to clear all.
    s.off('game_state');
    s.off('game_start');
    s.off('error');
    destroyRulesPanel(s);
  }
}
